import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { auth, signOut, signInWithEmailAndPassword, sendPasswordResetEmail, clearPersistence } from '../config/firebase';
import { getAuthErrorMessage, isSocialSignInCancelled } from '../utils/authErrors';
import { getCurrentAuthUser } from '../utils/authInstance';
import * as socialAuth from '../services/socialAuth';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  user: any;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  logout: () => Promise<void>;
  signup: (email: string, password: string, fullName: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  deleteAccount: (password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType & { refreshUser?: () => Promise<void> }>({
  isAuthenticated: false,
  isLoading: false,
  error: null,
  user: null,
  login: async () => ({ success: false }),
  signInWithGoogle: async () => {},
  signInWithApple: async () => {},
  logout: async () => {},
  signup: async () => {},
  resetPassword: async () => {},
  deleteAccount: async () => {},
  refreshUser: async () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Robust refreshUser: reloads user from Firebase and updates context state
  const refreshUser = useCallback(async () => {
    const currentUser = getCurrentAuthUser();
    if (currentUser) {
      try {
        await currentUser.reload();
        const updatedUser = getCurrentAuthUser();
        setUser(updatedUser);
        setIsAuthenticated(!!updatedUser);
      } catch (err) {
        console.error('AuthContext: Failed to reload user:', err);
        // Do NOT log out the user here! Only log out if there is truly no user.
        // Optionally, show an error message or keep the old user state
      }
    }
    // Only log out if there is truly no user
    else {
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Check for logout flag and force unauthenticated state on app startup
  useEffect(() => {
    const checkLogoutFlag = async () => {
      try {
        const logoutFlag = await AsyncStorage.getItem('force_logout');
        if (logoutFlag === 'true') {
          console.log('AuthContext: Found logout flag, forcing unauthenticated state');
          await AsyncStorage.removeItem('force_logout');
          await AsyncStorage.removeItem('last_logout_time'); // Added this line
          
          // Force unauthenticated state immediately
          setUser(null);
          setIsAuthenticated(false);
          setIsLoading(false);
          
          // Also try to clear Firebase persistence and sign out
          try {
            await clearPersistence();
            console.log('AuthContext: Cleared Firebase persistence on startup');
          } catch (error) {
            console.log('AuthContext: Failed to clear Firebase persistence on startup:', error);
          }
          
          const startupUser = getCurrentAuthUser();
          if (startupUser) {
            try {
              await signOut();
              console.log('AuthContext: Force signed out user on startup');
            } catch (error) {
              console.log('AuthContext: Failed to force sign out on startup:', error);
            }
          }
        }
      } catch (error) {
        console.error('AuthContext: Error checking logout flag:', error);
      }
    };
    
    checkLogoutFlag();
  }, []);

  // Listen for real Firebase auth state changes
  useEffect(() => {
    setIsLoading(true);
    const authInstance = typeof auth === 'function' ? auth() : auth;
    const unsubscribe = authInstance.onAuthStateChanged(async (user: any) => {
      // Simple auth state handling - let the startup useEffect handle logout flags
      setUser(user);
      setIsAuthenticated(!!user);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setError(null);

    // Clear any logout flags before attempting login
    try {
      await AsyncStorage.removeItem('force_logout');
      await AsyncStorage.removeItem('last_logout_time');
    } catch (clearError) {
      console.log('AuthContext: Error clearing logout flags before login:', clearError);
    }

    try {
      const userCredential = await signInWithEmailAndPassword(email, password);
      const signedInUser = userCredential?.user ?? userCredential?.userCredential?.user ?? null;
      if (!signedInUser) {
        throw new Error('Invalid email or password');
      }
      // onAuthStateChanged will sync isAuthenticated; set state for immediate navigation
      setUser(signedInUser);
      setIsAuthenticated(true);
      return { success: true };
    } catch (err) {
      console.error('🔐 Auth: Login error:', err);
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      setUser(null);
      setIsAuthenticated(false);
      throw new Error(errorMessage);
    }
  }, []);

  const completeSocialSignIn = useCallback(async (user: unknown) => {
    if (!user) {
      throw new Error('Sign-in did not return a user.');
    }
    setUser(user);
    setIsAuthenticated(true);
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setError(null);
    try {
      await AsyncStorage.removeItem('force_logout');
      await AsyncStorage.removeItem('last_logout_time');
      const user = await socialAuth.signInWithGoogle();
      await completeSocialSignIn(user);
    } catch (err) {
      if (socialAuth.isGoogleSignInCancelled(err) || isSocialSignInCancelled(err)) {
        return;
      }
      const errorMessage = getAuthErrorMessage(err, 'Google sign-in failed. Please try again.');
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [completeSocialSignIn]);

  const signInWithApple = useCallback(async () => {
    setError(null);
    try {
      await AsyncStorage.removeItem('force_logout');
      await AsyncStorage.removeItem('last_logout_time');
      const user = await socialAuth.signInWithApple();
      if (!user) {
        return;
      }
      await completeSocialSignIn(user);
    } catch (err) {
      if (socialAuth.isAppleSignInCancelled(err) || isSocialSignInCancelled(err)) {
        return;
      }
      const errorMessage = getAuthErrorMessage(err, 'Apple sign-in failed. Please try again.');
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [completeSocialSignIn]);

  // Web: Apple Sign-In disabled — Google-only on web (see socialAuth.web.ts).
  /*
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof socialAuth.consumeAppleRedirectSignIn !== 'function') {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const user = await socialAuth.consumeAppleRedirectSignIn();
        if (!cancelled && user) {
          await completeSocialSignIn(user);
        }
      } catch (err) {
        if (!cancelled) {
          const errorMessage = getAuthErrorMessage(err, 'Apple sign-in failed. Please try again.');
          setError(errorMessage);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [completeSocialSignIn]);
  */

  const signup = useCallback(async (email: string, password: string, fullName: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // For React Native Firebase, use auth().createUserWithEmailAndPassword
      const authInstance = typeof auth === 'function' ? auth() : auth;
      const userCredential = await authInstance.createUserWithEmailAndPassword(email, password);
      
      if (userCredential.user) {
        // Set display name immediately
        await userCredential.user.updateProfile({ displayName: fullName });
        // Force reload to get displayName reflected in user object
        await userCredential.user.reload();
        // Set user state with updated displayName
        setUser(userCredential.user);
        setIsAuthenticated(true);
        // Send verification email (but do not wait for it to complete before updating state)
        userCredential.user.sendEmailVerification().catch(() => {});
      }
    } catch (err) {
      console.error('🔐 Auth: Signup error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Signup failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    try {
      setIsLoading(true);
      setError(null);
      await sendPasswordResetEmail(email);
    } catch (err) {
      console.error('🔐 Auth: Password reset error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Password reset failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('AuthContext: Starting logout process');
      
      // Clear all user-related AsyncStorage data
      const keysToRemove = [
        'lastActiveTime',
        'app_settings',
        'goalmint_dev_subscription',
        'goalmint_dev_tier',
        'app_crash_recovery'
      ];
      
      // Also try to clear all AsyncStorage to remove any Firebase-related data
      try {
        const allKeys = await AsyncStorage.getAllKeys();
        console.log('AuthContext: Found AsyncStorage keys:', allKeys);
        
        // Remove all keys except the logout flag
        const keysToRemoveAll = allKeys.filter(key => key !== 'force_logout');
        await AsyncStorage.multiRemove(keysToRemoveAll);
        console.log('AuthContext: Cleared all AsyncStorage keys');
      } catch (error) {
        console.error('AuthContext: Error clearing all AsyncStorage:', error);
      }
      
      // Set logout flag and timestamp to clear persisted auth state on next app startup
      try {
        await AsyncStorage.setItem('force_logout', 'true');
        await AsyncStorage.setItem('last_logout_time', Date.now().toString());
        console.log('AuthContext: Set logout flag and timestamp for next app startup');
      } catch (error) {
        console.error('AuthContext: Error setting logout flag:', error);
      }
      
      // Get current user ID to clear user-specific settings
      const currentUser = getCurrentAuthUser();
      if (currentUser?.uid) {
        keysToRemove.push(`app_settings_${currentUser.uid}`);
      }
      
      // Clear all AsyncStorage keys
      for (const key of keysToRemove) {
        try {
          await AsyncStorage.removeItem(key);
          console.log(`AuthContext: Cleared AsyncStorage key: ${key}`);
        } catch (error) {
          console.error(`AuthContext: Error clearing AsyncStorage key ${key}:`, error);
        }
      }
      
      // Check if user is still authenticated before attempting to sign out
      if (!currentUser) {
        console.log('AuthContext: No authenticated user found, clearing local state only');
        setUser(null);
        setIsAuthenticated(false);
        return;
      }
      
      // Try to refresh the user token before logout to handle expired tokens
      try {
        console.log('AuthContext: Refreshing user token before logout');
        await currentUser.reload();
        console.log('AuthContext: Token refreshed successfully');
      } catch (refreshError) {
        console.log('AuthContext: Token refresh failed (likely expired), proceeding with logout:', refreshError);
        // Continue with logout even if token refresh fails
      }
      
      // Attempt to sign out from Firebase and clear persistence
      try {
        await signOut();
        console.log('AuthContext: Firebase signOut completed successfully');
        
        // Clear Firebase persistence to ensure no cached auth state
        try {
          await clearPersistence();
          console.log('AuthContext: Firebase persistence cleared successfully');
        } catch (persistenceError) {
          console.log('AuthContext: Firebase persistence clear failed (non-critical):', persistenceError);
        }
      } catch (signOutError) {
        console.log('AuthContext: Firebase signOut failed, but clearing local state:', signOutError);
        // Even if Firebase signOut fails, clear local state
      }
      
      // Always clear local state
      setUser(null);
      setIsAuthenticated(false);
      console.log('AuthContext: Logout completed successfully');
      
    } catch (err) {
      console.error('🔐 Auth: Logout error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Logout failed';
      setError(errorMessage);
      
      // Even on error, clear local state to ensure user is logged out
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteAccount = useCallback(async (password: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const currentUser = getCurrentAuthUser();
      if (currentUser) {
        await currentUser.delete();
        setUser(null);
        setIsAuthenticated(false);
      } else {
        throw new Error('No user is currently signed in.');
      }
    } catch (err) {
      console.error('🔐 Auth: Account deletion error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Account deletion failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value = {
    isAuthenticated,
    isLoading,
    error,
    user,
    login,
    signInWithGoogle,
    signInWithApple,
    logout,
    signup,
    resetPassword,
    deleteAccount,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
